import React, { useState } from 'react';
import { FaTimes, FaCopy, FaShare, FaEnvelope, FaWhatsapp, FaLink, FaCheckCircle } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../services/api';

const InviteModal = ({ event, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [emailData, setEmailData] = useState({
        subject: `Invitation: ${event?.name || 'Event'}`,
        message: `You are invited to attend ${event?.name || 'Event'} on ${event?.date ? new Date(event.date).toLocaleDateString() : 'N/A'} at ${event?.time || 'N/A'}. Venue: ${event?.venue || 'Not specified'}`
    });

    // ✅ Generate the registration link
    const generateInviteLink = () => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/register/event/${event._id}`;
    };

    // ✅ Copy link to clipboard
    const copyLink = () => {
        const link = generateInviteLink();
        navigator.clipboard.writeText(link).then(() => {
            setCopied(true);
            toast.success('✅ Link copied to clipboard!');
            setTimeout(() => setCopied(false), 3000);
        }).catch(() => {
            toast.error('Failed to copy link');
        });
    };

    // ✅ Share link via Web Share API
    const shareLink = async () => {
        const link = generateInviteLink();
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Invitation: ${event.name}`,
                    text: `You are invited to ${event.name} on ${new Date(event.date).toLocaleDateString()}`,
                    url: link
                });
                toast.success('✅ Shared successfully!');
            } else {
                copyLink();
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                toast.error('Failed to share. Please copy the link manually.');
            }
        }
    };

    // ✅ Share via WhatsApp
    const shareWhatsApp = () => {
        const link = generateInviteLink();
        const message = encodeURIComponent(
            `📢 *Invitation: ${event.name}*\n\n` +
            `📅 Date: ${new Date(event.date).toLocaleDateString()}\n` +
            `⏰ Time: ${event.time}\n` +
            `📍 Venue: ${event.venue || 'Not specified'}\n\n` +
            `🔗 Register here: ${link}\n\n` +
            `Please register to confirm your attendance.`
        );
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    // ✅ Send via Email
    const sendEmailInvite = async () => {
        if (!emailData.subject.trim() || !emailData.message.trim()) {
            toast.error('Please fill in subject and message');
            return;
        }

        setLoading(true);
        try {
            // In production, this would call your backend API to send emails
            // For now, we'll copy the link and show success
            const link = generateInviteLink();
            const fullMessage = `${emailData.message}\n\n🔗 Register here: ${link}`;
            
            // Copy the link for demo
            navigator.clipboard.writeText(link);
            
            toast.success('✅ Email content ready! Link copied to clipboard.');
            toast.success('📧 Paste the content in your email client.');
            
            // Log the email content
            console.log('Email Subject:', emailData.subject);
            console.log('Email Content:', fullMessage);
            
            setShowEmailForm(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            toast.error('Failed to prepare email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }} onClick={onClose}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '550px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }} onClick={(e) => e.stopPropagation()}>
                {/* Close Button */}
                <button 
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '15px',
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#666'
                    }}
                >
                    <FaTimes />
                </button>

                {/* Header */}
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ 
                        color: '#0a1e3c', 
                        fontFamily: "'Times New Roman', Times, serif",
                        fontWeight: 700,
                        fontSize: '20px',
                        margin: 0
                    }}>
                        <FaLink /> Generate Invite
                    </h3>
                    <p style={{ color: '#666', fontSize: '13px', marginTop: '5px' }}>
                        Share this link with students to register for the event
                    </p>
                </div>

                {/* Event Details */}
                <div style={{
                    background: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    borderLeft: '4px solid #c9a84c'
                }}>
                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#0a1e3c' }}>
                        {event.name}
                    </p>
                    <p style={{ margin: '0 0 3px 0', fontSize: '13px', color: '#666' }}>
                        📅 {new Date(event.date).toLocaleDateString()} | ⏰ {event.time}
                    </p>
                    <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                        📍 {event.venue || 'Not specified'}
                    </p>
                </div>

                {/* Link Display */}
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    background: '#f0f2f5',
                    padding: '10px 15px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    marginBottom: '15px'
                }}>
                    <input
                        type="text"
                        value={generateInviteLink()}
                        readOnly
                        style={{
                            flex: 1,
                            border: 'none',
                            background: 'transparent',
                            padding: '5px',
                            fontSize: '12px',
                            color: '#333',
                            outline: 'none',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all'
                        }}
                    />
                    <button
                        onClick={copyLink}
                        style={{
                            padding: '6px 12px',
                            background: copied ? '#28a745' : '#0a1e3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            transition: 'all 0.3s',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {copied ? <FaCheckCircle /> : <FaCopy />} {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>

                {/* Share Options */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px',
                    marginBottom: '15px'
                }}>
                    <button
                        onClick={shareLink}
                        className="btn btn-primary"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px'
                        }}
                    >
                        <FaShare /> Share
                    </button>
                    <button
                        onClick={shareWhatsApp}
                        className="btn btn-success"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px',
                            background: '#25D366'
                        }}
                    >
                        <FaWhatsapp /> WhatsApp
                    </button>
                    <button
                        onClick={() => setShowEmailForm(!showEmailForm)}
                        className="btn btn-warning"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            padding: '10px',
                            gridColumn: 'span 2'
                        }}
                    >
                        <FaEnvelope /> Email Invite
                    </button>
                </div>

                {/* Email Form */}
                {showEmailForm && (
                    <div style={{
                        marginTop: '15px',
                        padding: '15px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                    }}>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '13px' }}>
                                Subject
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={emailData.subject}
                                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, fontSize: '13px' }}>
                                Message
                            </label>
                            <textarea
                                className="form-control"
                                rows="4"
                                value={emailData.message}
                                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ddd', resize: 'vertical' }}
                            />
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                            💡 The registration link will be automatically appended to your message.
                        </div>
                        <button
                            onClick={sendEmailInvite}
                            className="btn btn-success"
                            style={{ width: '100%', padding: '10px' }}
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Invite'}
                        </button>
                    </div>
                )}

                {/* Instructions */}
                <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: '#fff3cd',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#856404'
                }}>
                    <strong>📌 How it works:</strong>
                    <ol style={{ margin: '5px 0 0 15px', padding: 0 }}>
                        <li>Share the link with students</li>
                        <li>Students register using the link</li>
                        <li>Registrations appear in Admin Portal</li>
                        <li>Admin approves/activates students</li>
                    </ol>
                </div>
            </div>
        </div>
    );
};

export default InviteModal;